import {
  DEFAULT_WALK_PHYSICS,
  Vec3,
  type CameraOptions,
  type CollisionWorld,
  type WalkPhysicsConfig,
} from '@mindcloudxai/shared';
import type { NavigationMode, NavigationModeContext, NavigationModeKey } from './types';
import { NoopCollisionWorld } from './CollisionWorld';
import { clamp, direction, normalize } from './shared';

export class WalkMode implements NavigationMode {
  readonly key: NavigationModeKey = 'walk';

  private ctx: NavigationModeContext | null = null;
  private pos: Vec3 = new Vec3(0, 1.6, 4);
  private yaw = 0;
  private pitch = 0;
  private keys = new Set<string>();
  private isPointerLocked = false;
  private isTouchLooking = false;
  private isTouchMoving = false;
  private isThirdPerson = false;
  private rafId: number | null = null;
  private lastFrame = 0;
  private pointerDownX = 0;
  private pointerDownY = 0;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private touchMoveX = 0;
  private touchMoveZ = 0;
  private collision: CollisionWorld = new NoopCollisionWorld();
  private physics: Required<WalkPhysicsConfig> = { ...DEFAULT_WALK_PHYSICS };
  private velocityY = 0;
  private isGrounded = true;

  setCollisionWorld(world: CollisionWorld): void {
    this.collision = world;
  }

  setPhysics(config: WalkPhysicsConfig): void {
    Object.assign(this.physics, config);
  }

  activate(ctx: NavigationModeContext) {
    this.ctx = ctx;
    this.syncFromCamera();
    const el = ctx.canvas;
    this.isPointerLocked = document.pointerLockElement === el;

    el.addEventListener('pointerdown', this.onPointerDown);
    el.addEventListener('pointermove', this.onPointerMove);
    el.addEventListener('pointerup', this.onPointerUp);
    el.addEventListener('pointercancel', this.onPointerCancel);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);

    this.startLoop();
  }

  deactivate() {
    if (!this.ctx) return;
    const el = this.ctx.canvas;
    el.removeEventListener('pointerdown', this.onPointerDown);
    el.removeEventListener('pointermove', this.onPointerMove);
    el.removeEventListener('pointerup', this.onPointerUp);
    el.removeEventListener('pointercancel', this.onPointerCancel);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);

    if (document.pointerLockElement === el) document.exitPointerLock();
    this.isPointerLocked = false;
    this.isTouchLooking = false;
    this.isTouchMoving = false;
    this.touchMoveX = 0;
    this.touchMoveZ = 0;
    this.keys.clear();
    this.stopLoop();
    this.ctx = null;
  }

  private syncFromCamera() {
    if (!this.ctx) return;
    const camera = this.ctx.renderer.getCamera();
    this.pos = Vec3.from(camera.position);
    if ('setGroundY' in this.collision) {
      (this.collision as NoopCollisionWorld).setGroundY(this.pos.y - this.physics.eyeHeight);
    }
    const dir = direction(camera);
    this.yaw = Math.atan2(dir.x, -dir.z);
    this.pitch = Math.asin(clamp(dir.y, -1, 1));
    this.pos = Vec3.from(this.collision.snapToGround(this.pos, this.physics.eyeHeight));
  }

  private onPointerDown = (e: PointerEvent) => {
    if (!this.ctx) return;
    this.pointerDownX = e.clientX;
    this.pointerDownY = e.clientY;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
    if (e.pointerType !== 'mouse') {
      const rect = this.ctx.canvas.getBoundingClientRect();
      this.isTouchMoving = e.clientX < rect.left + rect.width * 0.45;
      this.isTouchLooking = !this.isTouchMoving;
      this.ctx.canvas.setPointerCapture(e.pointerId);
    }
  };

  private onPointerMove = (e: PointerEvent) => {
    if (!this.isTouchLooking && !this.isTouchMoving) return;
    const dx = e.clientX - this.lastPointerX;
    const dy = e.clientY - this.lastPointerY;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
    if (this.isTouchLooking) {
      this.applyLookDelta(dx, dy);
      return;
    }
    this.touchMoveX = clamp((e.clientX - this.pointerDownX) / 48, -1, 1);
    this.touchMoveZ = clamp((this.pointerDownY - e.clientY) / 48, -1, 1);
  };

  private onPointerUp = (e: PointerEvent) => {
    if (!this.ctx) return;
    if (this.ctx.canvas.hasPointerCapture(e.pointerId)) {
      this.ctx.canvas.releasePointerCapture(e.pointerId);
    }
    const moved = Math.hypot(e.clientX - this.pointerDownX, e.clientY - this.pointerDownY) > 6;
    this.isTouchLooking = false;
    this.isTouchMoving = false;
    this.touchMoveX = 0;
    this.touchMoveZ = 0;
    if (e.pointerType === 'mouse' && e.button === 0 && !moved) {
      void this.ctx.canvas.requestPointerLock();
    }
  };

  private onPointerCancel = (e: PointerEvent) => {
    if (this.ctx?.canvas.hasPointerCapture(e.pointerId)) {
      this.ctx.canvas.releasePointerCapture(e.pointerId);
    }
    this.isTouchLooking = false;
    this.isTouchMoving = false;
    this.touchMoveX = 0;
    this.touchMoveZ = 0;
  };

  private onPointerLockChange = () => {
    this.isPointerLocked = document.pointerLockElement === this.ctx?.canvas;
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.isPointerLocked) return;
    this.applyLookDelta(e.movementX, e.movementY);
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Tab') {
      if (!this.isPointerLocked) return;
      if (!e.repeat) this.isThirdPerson = !this.isThirdPerson;
      e.preventDefault();
      return;
    }
    if (e.code === 'Space') e.preventDefault();
    this.keys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private applyLookDelta(dx: number, dy: number) {
    const sensitivity = 0.002;
    this.yaw += dx * sensitivity;
    this.pitch = clamp(this.pitch - dy * sensitivity, -Math.PI / 2 + 0.05, Math.PI / 2 - 0.05);
  }

  private startLoop() {
    this.lastFrame = performance.now();
    const loop = (now: number) => {
      this.update(now);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private stopLoop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private update(now: number) {
    if (!this.ctx) return;
    const dt = Math.min((now - this.lastFrame) / 1000, 0.05);
    this.lastFrame = now;

    const { walkSpeed, sprintSpeed, jumpSpeed, gravity, eyeHeight } = this.physics;
    const isSprinting = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    const speed = isSprinting ? sprintSpeed : walkSpeed;

    if (this.isGrounded && this.keys.has('Space')) {
      this.velocityY = jumpSpeed;
      this.isGrounded = false;
    }

    const fwdX = Math.sin(this.yaw);
    const fwdZ = -Math.cos(this.yaw);
    const rightX = Math.cos(this.yaw);
    const rightZ = Math.sin(this.yaw);
    let moveX = 0;
    let moveZ = 0;

    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) {
      moveX += fwdX;
      moveZ += fwdZ;
    }
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) {
      moveX -= fwdX;
      moveZ -= fwdZ;
    }
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) {
      moveX -= rightX;
      moveZ -= rightZ;
    }
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) {
      moveX += rightX;
      moveZ += rightZ;
    }
    if (this.touchMoveZ !== 0 || this.touchMoveX !== 0) {
      moveX += fwdX * this.touchMoveZ + rightX * this.touchMoveX;
      moveZ += fwdZ * this.touchMoveZ + rightZ * this.touchMoveX;
    }

    const moveLen = Math.hypot(moveX, moveZ);
    let next: Vec3 = this.pos;
    if (moveLen > 0) {
      next = new Vec3(
        this.pos.x + (moveX / moveLen) * speed * dt,
        this.pos.y,
        this.pos.z + (moveZ / moveLen) * speed * dt,
      );
      next = Vec3.from(this.collision.move(this.pos, next));
    }

    this.velocityY -= gravity * dt;
    next = new Vec3(next.x, next.y + this.velocityY * dt, next.z);

    const grounded = this.collision.snapToGround(next, eyeHeight);
    if (next.y <= grounded.y) {
      next = Vec3.from(grounded);
      this.velocityY = 0;
      this.isGrounded = true;
    } else {
      this.isGrounded = false;
    }

    this.pos = next;
    this.applyCamera();
  }

  private cameraFromState(dir: Vec3, base?: CameraOptions): CameraOptions {
    if (!this.isThirdPerson) {
      return {
        position: this.pos,
        target: this.pos.add(dir),
        fov: base?.fov,
        near: base?.near,
        far: base?.far,
      };
    }

    const horizontal = normalize(new Vec3(dir.x, 0, dir.z));
    const position = new Vec3(
      this.pos.x - horizontal.x * 3,
      this.pos.y + 1,
      this.pos.z - horizontal.z * 3,
    );

    return {
      position,
      target: new Vec3(this.pos.x + horizontal.x, this.pos.y, this.pos.z + horizontal.z),
      fov: base?.fov,
      near: base?.near,
      far: base?.far,
    };
  }

  private applyCamera() {
    if (!this.ctx) return;
    const dir = new Vec3(
      Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch),
    );
    this.ctx.renderer.setCamera(this.cameraFromState(dir, this.ctx.renderer.getCamera()));
  }
}
